import React from 'react';
import { Button, Group, Text, Badge } from '@mantine/core';
import { IconCurrencyDollar, IconFileExport, IconRefresh } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

/**
 * BillingActionButtons - Action buttons for billing management
 * Part of BL-002 Admin Billing Reports Feature - Step 43: Updated for batch workflow
 */
interface BillingActionButtonsProps {
    selectedOrders: string[];
    onBillAndExport?: (orderIds: string[]) => Promise<void>;
    onMarkAsBilled?: (orderIds: string[]) => void; // Legacy support
    onExportSelected?: (orderIds: string[]) => void; // Legacy support
    onRefresh?: () => void;
    isLoading?: boolean;
}

const BillingActionButtons: React.FC<BillingActionButtonsProps> = ({
    selectedOrders,
    onBillAndExport,
    onMarkAsBilled,
    onExportSelected,
    onRefresh,
    isLoading = false
}) => {
    const { t } = useTranslation();
    const hasSelectedOrders = selectedOrders.length > 0;

    const handleBillAndExport = async () => {
        if (hasSelectedOrders && onBillAndExport) 
            await onBillAndExport(selectedOrders);
        
    };

    const handleMarkAsBilled = () => {
        if (hasSelectedOrders && onMarkAsBilled) 
            onMarkAsBilled(selectedOrders);
        
    };

    const handleExportSelected = () => {
        if (hasSelectedOrders && onExportSelected) 
            onExportSelected(selectedOrders);
        
    };

    const handleRefresh = () => {
        if (onRefresh) 
            onRefresh();
        
    };

    return (
        <Group justify="space-between" align="center">
            <Group gap="md">
                {hasSelectedOrders && (
                    <Badge
                        color="blue"
                        variant="light"
                        size="lg"
                    >
                        {t('billing.selection.count', { count: selectedOrders.length })}
                    </Badge>
                )}
                {!hasSelectedOrders && (
                    <Text size="sm" c="dimmed">
                        {t('billing.selection.prompt')}
                    </Text>
                )}
            </Group>

            <Group gap="sm">
                <Button
                    variant="light"
                    color="gray"
                    leftSection={<IconRefresh size={16} />}
                    onClick={handleRefresh}
                    loading={isLoading}
                >
                    {t('billing.actions.refresh')}
                </Button>

                {/* Primary action: Combined Bill & Export */}
                {onBillAndExport ? (
                    <Button
                        color="green"
                        leftSection={<IconFileExport size={16} />}
                        onClick={handleBillAndExport}
                        disabled={!hasSelectedOrders || isLoading}
                        loading={isLoading}
                    >
                        {t('billing.actions.billAndExport')}
                    </Button>
                ) : (
                    /* Legacy separate actions for backward compatibility */
                    <>
                        <Button
                            variant="light"
                            color="blue"
                            leftSection={<IconFileExport size={16} />}
                            onClick={handleExportSelected}
                            disabled={!hasSelectedOrders || isLoading}
                        >
                            {t('billing.actions.exportSelected')}
                        </Button>

                        <Button
                            color="green"
                            leftSection={<IconCurrencyDollar size={16} />}
                            onClick={handleMarkAsBilled}
                            disabled={!hasSelectedOrders || isLoading}
                        >
                            {t('billing.actions.markAsBilled')}
                        </Button>
                    </>
                )}
            </Group>
        </Group>
    );
};

export default BillingActionButtons; 